import React, { useContext } from "react";

import _ from "app/lang";
import { Site, User } from "app/models";
import { pageContext, PartialContext } from "app/page/ContextProvider";

import DrawerMenu, { DrawerMenuItem } from "./DrawerMenu";
import { SimpleItem } from "./SimpleMenuItem";

export function getMenuItems(context: PartialContext) {
    if (!context.site) {
        return [];
    }

    const user = new User(context);
    const site = new Site(context.site);
    const legacyUrl = "/site/:siteId";
    const fe2Url = "/v2/cloud/:cloudId/site/:siteId";
    const cloud_type = context.cloud_type;

    const items: DrawerMenuItem[] = [
        {
            icon: "dashboard",
            text: _("Dashboard"),
            href: `${legacyUrl}/dashboard`
        },
        {
            icon: "ignite_ap",
            text: _("Devices"),
            href: `${fe2Url}/devices`
        },
        {
            icon: "list_alt",
            text: _("Activity"),
            href: `${legacyUrl}/activity`
        },
        {
            icon: "signal_wifi_3_bar",
            text: _("Wireless Clients"),
            href: `${legacyUrl}/clients`
        },
    ];

    if (user.hasPermission("edit_site")) {
        items.push(
            {
                text: _("Manage")
            },
        );
    }

    items.push(
        {
            icon: "place",
            text: _("Maps"),
            subitems: [
                {
                    icon: "google_maps",
                    text: _("Google Map"),
                    href: `${legacyUrl}/google-map`
                },
                {
                    icon: "map",
                    text: _("Floor Maps"),
                    href: `${legacyUrl}/floor-maps`
                },
                {
                    icon: "device_hub",
                    text: _("Topology"),
                    href: `${fe2Url}/topology`
                }
            ]
        }
    );

    if (user.hasPermission("edit_site")) {
        items.push(
            {
                icon: "edit_attributes",
                text: _("Site Properties"),
                href: `${fe2Url}/edit`
            },
            {
                icon: "notifications",
                text: _("Notifications"),
                href: `${fe2Url}/notification-settings`
            }
        );
    }

    // Check if cloud is in API mode to filter OpenWiFi items
    const isApiMode = context.cloud?.configuration === "api";

    if (site.configEnabled && user.hasPermission("view_config")) {
        let drawerItems: SimpleItem[];
        if (cloud_type === "Origin") {
            drawerItems = [
                {
                    icon: "settings",
                    text: _("General"),
                    href: `${legacyUrl}/config#general-locale`
                },
                {
                    icon: "wifi5",
                    text: _("WiFi5"),
                    href: `${legacyUrl}/config#wifi5-wireless`
                },
                {
                    icon: "wifi6",
                    text: _("WiFi6"),
                    href: `${legacyUrl}/config#wifi6-wireless`
                },
                {
                    icon: "metrolinq",
                    text: _("MetroLinq"),
                    href: `${legacyUrl}/config#ml-wireless`
                },
                {
                    icon: "glinq",
                    text: _("GLinq"),
                    href: `${legacyUrl}/config#glinq-wireless`
                },
                {
                    icon: "switch",
                    text: _("Switch"),
                    href: `${legacyUrl}/config#switch-security`
                }
            ];
        } else if (cloud_type === "Openwifi") {
            drawerItems = [
                {
                    icon: "settings",
                    text: _("General"),
                    href: `${legacyUrl}/config#general-locale`
                }
            ];
            // Only add OpenWiFi menu item if not in API mode
            if (!isApiMode) {
                drawerItems.push({
                    icon: "openwifi",
                    text: _("OpenWiFi"),
                    href: `${legacyUrl}/config#radio`
                });
            }
            // {
            //     icon: "switch",
            //     text: _("OpenLan Switch"),
            //     href: `${legacyUrl}/config#openlan-ethernet`
            // }
        } else if (cloud_type === "Hybrid") {
            drawerItems = [
                {
                    icon: "settings",
                    text: _("General"),
                    href: `${legacyUrl}/config#general-locale`
                },
                {
                    icon: "wifi5",
                    text: _("WiFi5"),
                    href: `${legacyUrl}/config#wifi5-wireless`
                },
                {
                    icon: "wifi6",
                    text: _("WiFi6"),
                    href: `${legacyUrl}/config#wifi6-wireless`
                }
            ];
            // Only add OpenWiFi and OpenLan menu items if not in API mode
            if (!isApiMode) {
                drawerItems.push(
                    {
                        icon: "openwifi",
                        text: _("OpenWiFi"),
                        href: `${legacyUrl}/config#openwifi-import`
                    },
                    {
                        icon: "switch",
                        text: _("OpenLan Switch"),
                        href: `${legacyUrl}/config#openlan-import`
                    }
                );
            }
            drawerItems.push(
                {
                    icon: "metrolinq",
                    text: _("MetroLinq"),
                    href: `${legacyUrl}/config#ml-wireless`
                },
                {
                    icon: "glinq",
                    text: _("GLinq"),
                    href: `${legacyUrl}/config#glinq-wireless`
                },
                {
                    icon: "switch",
                    text: _("Switch"),
                    href: `${legacyUrl}/config#switch-security`
                }
            );
        } else {
            drawerItems = [];
        }

        const configItem: DrawerMenuItem = {
            icon: "build",
            text: _("Configuration"),
            subitems: drawerItems
        };

        items.splice(2, 0, configItem);
    }

    return items;
}

export default function SiteMenu() {
    const context = useContext(pageContext);
    const items = getMenuItems(context);

    return <DrawerMenu items={items} />;
}
